import React from 'react';

export interface DateRange {
  /** Inclusive YYYY-MM-DD bounds. */
  start: string;
  end: string;
}

const iso = (d: Date): string => d.toISOString().slice(0, 10);

/** end = today, start = today - (days-1). */
export const rangeForDays = (days: number): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - (days - 1));
  return { start: iso(start), end: iso(end) };
};

const PRESETS: { label: string; days: number }[] = [
  { label: '7d', days: 7 },
  { label: '30d', days: 30 },
  { label: '90d', days: 90 },
  { label: '12mo', days: 365 },
];

/**
 * Compact analytics date-range control: preset chips + custom start/end inputs.
 * Purely controlled — the parent owns the range and refetches on change.
 */
const DateRangeControl: React.FC<{
  value: DateRange;
  onChange: (next: DateRange) => void;
  className?: string;
  /** Show an "All" chip that clears the range (empty start/end = no filter). */
  includeAllTime?: boolean;
}> = ({ value, onChange, className, includeAllTime }) => {
  const activeDays = PRESETS.find((p) => {
    const r = rangeForDays(p.days);
    return r.start === value.start && r.end === value.end;
  })?.days;
  const isAll = !value.start && !value.end;

  return (
    <div className={`flex flex-wrap items-center gap-2 ${className ?? ''}`}>
      <div className="flex items-center gap-1 rounded-lg border border-[#E9E6F4] bg-white p-0.5">
        {includeAllTime && (
          <button
            type="button"
            onClick={() => onChange({ start: '', end: '' })}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
              isAll ? 'bg-violet-600 text-white' : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            All
          </button>
        )}
        {PRESETS.map((p) => (
          <button
            key={p.days}
            type="button"
            onClick={() => onChange(rangeForDays(p.days))}
            className={`rounded-md px-2.5 py-1 text-xs font-semibold transition-colors ${
              activeDays === p.days
                ? 'bg-violet-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>
      <div className="flex items-center gap-1.5 text-xs text-slate-500">
        <input
          type="date"
          value={value.start}
          max={value.end}
          onChange={(e) => onChange({ ...value, start: e.target.value })}
          aria-label="Start date"
          className="rounded-lg border border-[#E9E6F4] bg-white px-2 py-1 text-slate-700 focus:border-violet-400 focus:outline-none"
        />
        <span>→</span>
        <input
          type="date"
          value={value.end}
          min={value.start}
          onChange={(e) => onChange({ ...value, end: e.target.value })}
          aria-label="End date"
          className="rounded-lg border border-[#E9E6F4] bg-white px-2 py-1 text-slate-700 focus:border-violet-400 focus:outline-none"
        />
      </div>
    </div>
  );
};

export default DateRangeControl;
